import classNames from "classnames";
import React, { useEffect, useState } from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import iconMenuBudget from "../../../../img/icons/mattress-match/menu-budget.svg";
import iconMenuCool from "../../../../img/icons/mattress-match/menu-cooling.svg";
import iconMenuHeight from "../../../../img/icons/mattress-match/menu-height.svg";
import iconMenuRestart from "../../../../img/icons/mattress-match/menu-restart.svg";
import iconMenuResults from "../../../../img/icons/mattress-match/menu-results.svg";
// Icons
import iconMenuSleep from "../../../../img/icons/mattress-match/menu-sleep.svg";
import iconMenuWeight from "../../../../img/icons/mattress-match/menu-weight.svg";
import caretRight from "../../../../img/misc/caret-right.svg";
import { IMattressMatchPreferenceSet } from "../../../models/catalogue";
import { slugify } from "../../../utils/text";
import { useAppDispatch, useAppSelector } from "../../reducers";
import {
    CoolnessOptions,
    MattressSizeOptions,
    Step,
    stepInformation,
    stepOrder,
} from "../constants";
import { defaults } from "../defaults";
import { ISelections } from "../models";
import {
    getResultsFromPreferences,
    onNavigate,
    resetResults,
    restartQuiz,
} from "../reducers";

import styles from "./NavButton.module.scss";

interface IProps {
    step: Step;
    selectionData: keyof ISelections;
}

const icons = {
    "menu-sleep": iconMenuSleep,
    "menu-height": iconMenuHeight,
    "menu-weight": iconMenuWeight,
    "menu-cooling": iconMenuCool,
    "menu-budget": iconMenuBudget,
    "menu-restart": iconMenuRestart,
    "menu-results": iconMenuResults,
} as const;

const iconExists = (step: string): step is keyof typeof icons => {
    return Object.prototype.hasOwnProperty.call(icons, step);
};

const getIcon = (trackerStep: string): string | null => {
    const step = trackerStep.toLowerCase().replace(/\s+/g, "-");
    return iconExists(step) ? icons[step] : null;
};

const STEP_LABELS: {
    [key in Step]: string;
} = {
    [Step.SIZE]: t`Mattress Size`,
    [Step.SLEEP]: t`Sleep Positions`,
    [Step.HEIGHT]: t`Height`,
    [Step.WEIGHT]: t`Weight`,
    [Step.COOLING]: t`Cooling`,
    [Step.BUDGET]: t`Budget`,
    [Step.RESULTS]: t`Results`,
} as const;

export const NavButton = (props: IProps) => {
    const { selections, currentScreen } = useAppSelector(
        (state) => state.mattressmatch.ui,
    );
    const stepCount = Object.values(Step).length;
    const dispatch = useAppDispatch();
    const [isStepComplete, setIsStepComplete] = useState<boolean>(false);
    useEffect(() => {
        if (props.step === Step.SIZE) {
            setIsStepComplete(true);
            return;
        }
        if (props.step === Step.RESULTS) {
            setIsStepComplete(false);
            return;
        }
        setIsStepComplete(
            selections[props.selectionData] !==
                defaults.ui.selections[props.selectionData],
        );
    }, [props.selectionData, props.step, selections[props.selectionData]]);
    if (props.step === Step.SIZE) {
        return null;
    }
    const stepIdx = stepOrder.indexOf(props.step);
    const isResultsPage = props.step === Step.RESULTS;
    const isDisabled =
        (stepIdx !== currentScreen && !isStepComplete) ||
        stepIdx > currentScreen;
    const icon = getIcon(`menu-${slugify(props.step)}`);

    const buttonClasses = classNames({
        [styles.button]: true,
        completed: isStepComplete,
        active: stepIdx === currentScreen,
    });
    const previousScreen = STEP_LABELS[stepOrder[currentScreen - 1]];
    const nextScreen = STEP_LABELS[stepOrder[currentScreen + 1]];

    const loadResults = () => {
        const cooling = stepInformation[Step.COOLING].data.find(
            (item) => item.level === selections.selectedCoolness,
        )?.value;
        const data: IMattressMatchPreferenceSet = {
            size: Object.values(MattressSizeOptions).includes(
                selections.selectedSize,
            )
                ? selections.selectedSize
                : "",
            sleep_position:
                stepInformation[Step.SLEEP].data.find(
                    (item) => item.level === selections.selectedSleepPosition,
                )?.value || "",
            height:
                stepInformation[Step.HEIGHT].data.find(
                    (item) => item.level === selections.selectedHeight,
                )?.value || "",
            weight:
                stepInformation[Step.WEIGHT].data.find(
                    (item) => item.level === selections.selectedWeight,
                )?.value || "",
            cooling: cooling ? cooling !== CoolnessOptions.NEVER : false, // If the received value indicates sometimes or always sleep hot, those inputs will be weighted the same and be accepted as true for cooling while matching conditions.
            price_level:
                stepInformation[Step.BUDGET].data.find(
                    (item) => item.level === selections.selectedBudget,
                )?.value || "",
        };
        dispatch(getResultsFromPreferences(data));
    };

    const navigateTo = (screen: number) => {
        if (stepOrder[currentScreen + 1] === Step.RESULTS) {
            loadResults();
        }
        if (stepOrder[currentScreen] === Step.RESULTS) {
            dispatch(resetResults());
        }
        dispatch(
            onNavigate({
                currentScreen: screen,
            }),
        );
    };
    const restart = () => {
        dispatch(restartQuiz());
    };

    return (
        <div className={styles.container}>
            <div className={styles.innerContainer}>
                <div className={styles.buttonContainer}>
                    <button
                        key={currentScreen - 1}
                        onClick={() => {
                            navigateTo(currentScreen - 1);
                        }}
                        className={`${buttonClasses} left`}
                        data-key={props.step}
                        disabled={isDisabled}
                    >
                        {previousScreen}
                        <SVG src={caretRight} />
                    </button>
                </div>
                <div className={styles.content}>
                    <div className={styles.svgContainer}>
                        {icon && (
                            <SVG
                                src={icon}
                                title={props.step}
                                data-key={props.step}
                            />
                        )}
                    </div>
                    <span className={styles.stepName}>
                        {STEP_LABELS[props.step]}
                    </span>
                    {isResultsPage ? (
                        <span className={styles.questionSteps}>
                            Result 2 of 2
                        </span>
                    ) : (
                        <span className={styles.questionSteps}>
                            Question {currentScreen + 1} of {stepCount - 1}{" "}
                        </span>
                    )}
                </div>
                <div className={styles.buttonContainer}>
                    {isResultsPage ? (
                        <button
                            key={props.step}
                            onClick={() => {
                                restart();
                            }}
                            className={`${styles.button} restart`}
                            data-key={props.step}
                            disabled={isDisabled}
                        >
                            <span>{t`Restart`}</span>
                            <SVG src={iconMenuRestart} />
                        </button>
                    ) : (
                        <button
                            key={currentScreen + 1}
                            onClick={() => {
                                navigateTo(currentScreen + 1);
                            }}
                            className={`${buttonClasses} right`}
                            data-key={props.step}
                            disabled={isDisabled}
                        >
                            <span>{nextScreen}</span>
                            <SVG src={caretRight} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
